import {
    Box,
    Table,
    Typography,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    CircularProgress
} from "@mui/material";
import { useLocation } from "react-router-dom";
import React from "react";
import { useDispatch } from "react-redux";
import * as Action from "../../actions/adminAction";
import { BASE_URL } from "../../utils/urls";

const borderSx = "2px solid";
const videoCategories = [
    'Teacher or Principal video',
    'Training video',
    'Test 1 video',
    'Test 2 video',
    'Teacher/Principal Video (Monthly)',
    'Students Video (Weekly)'
];

// ---- Signature constants (aligned with FormFill) ----
const MAX_SIGNATURE_SIZE_MB = 5;
const ALLOWED_SIGNATURE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

const SIGNATURE_STANDARD_WIDTH = 300;
const SIGNATURE_STANDARD_HEIGHT = 100;
const SIGNATURE_MIN_WIDTH = 150;
const SIGNATURE_MIN_HEIGHT = 40;
const SIGNATURE_MAX_WIDTH = 600;
const SIGNATURE_MAX_HEIGHT = 200;
const SIGNATURE_MIN_RATIO = 1.2;
const SIGNATURE_MAX_RATIO = 6;

// ---- Editable field visual constants ----
const EDITABLE_BG = "#e3f2fd";
const EDITABLE_BORDER_COLOR = "#42a5f5";
const EDITABLE_HOVER_BORDER = "#1e88e5";
const EDITABLE_FOCUS_BORDER = "#1565c0";
const EDITABLE_ICON_COLOR = "#1976d2";
const EDITABLE_PLACEHOLDER = "येथे संपादित करा...";

// ---- Small helpers ----
const isPlainObject = (v) =>
    v !== null && typeof v === "object" && !Array.isArray(v);

// ---- Signature upload helper ----
const uploadSignatureToServer = async (file, trainingId) => {
    const url = `${BASE_URL}/upload/photo`;
    const formData = new FormData();
    formData.append("image", file);
    formData.append("fileName", `trainer_signature_${trainingId || Date.now()}`);

    const res = await fetch(url, { method: "POST", body: formData });
    if (!res.ok) throw new Error("Signature upload failed");
    const data = await res.json();
    return data.url;
};

// ---- Signature dimension validation (matches FormFill) ----
const inspectSignatureImage = (file) =>
    new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            const width = img.naturalWidth;
            const height = img.naturalHeight;
            URL.revokeObjectURL(objectUrl);

            let error = "";

            if (width < SIGNATURE_MIN_WIDTH || height < SIGNATURE_MIN_HEIGHT) {
                error = `Signature is too small (${width}×${height}px). Minimum allowed is ${SIGNATURE_MIN_WIDTH}×${SIGNATURE_MIN_HEIGHT}px.`;
            } else if (width > SIGNATURE_MAX_WIDTH || height > SIGNATURE_MAX_HEIGHT) {
                error = `Signature is too large (${width}×${height}px). Maximum allowed is ${SIGNATURE_MAX_WIDTH}×${SIGNATURE_MAX_HEIGHT}px.`;
            } else {
                const ratio = width / height;
                if (ratio < SIGNATURE_MIN_RATIO || ratio > SIGNATURE_MAX_RATIO) {
                    error = `Signature aspect ratio looks unusual (${ratio.toFixed(2)}:1). Please use an image close to ${SIGNATURE_STANDARD_WIDTH}×${SIGNATURE_STANDARD_HEIGHT}px (3:1).`;
                }
            }

            resolve({ width, height, error });
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("Could not read the signature image. Please try another file."));
        };

        img.src = objectUrl;
    });

/**
 * ✅ Rebuilds session rows from report data.
 */
const buildSessionRowsFromReport = (result) => {
    const trainingDetails = Array.isArray(result?.trainingDetails)
        ? result.trainingDetails
        : [];

    const तपशील =
        result?.beneficiaryData?.["तपशील"] ||
        result?.totalBeneficiaryData?.["तपशील"] ||
        result?.assignTrainerObject?.beneficiaryData?.["तपशील"];

    const categoryToSession = {
        Theory: "सैद्धांतिक",
        Practical: "प्रात्यक्षिक",
        Field: "प्रक्षेत्र",
    };

    const sessionOrder = ["सैद्धांतिक", "प्रात्यक्षिक", "प्रक्षेत्र"];

    const savedById = {};
    const savedByName = {};
    const savedSessionSubject = {};
    const savedSessionNumber = {};
    const savedModulesBySession = {
        "सैद्धांतिक": [],
        "प्रात्यक्षिक": [],
        "प्रक्षेत्र": [],
    };

    if (Array.isArray(तपशील)) {
        तपशील.forEach((s) => {
            const st = s?.sessionType;
            if (!st) return;

            savedSessionSubject[st] = s?.subject ?? "";
            savedSessionNumber[st] = s?.plannedSessions ?? "";

            (s?.modules || []).forEach((m) => {
                const planned = m?.plannedSessions ?? "";
                const id = m?.moduleId || m?.trainingModule_id || m?.module?._id;
                const name = m?.moduleName || m?.module?.trainingModuleName;

                if (id) savedById[id] = planned;
                if (name) savedByName[name] = planned;

                if (savedModulesBySession[st]) {
                    savedModulesBySession[st].push({
                        moduleId: id || "",
                        moduleName: name || "-",
                        plannedSessions: planned,
                    });
                }
            });
        });
    }

    const resolvePlanned = (td) => {
        const ids = [td?.trainingModule_id, td?._id, td?.moduleId].filter(Boolean);
        for (const id of ids) {
            if (savedById[id] !== undefined) return savedById[id];
        }
        const name = td?.trainingModuleName || td?.moduleName;
        if (name && savedByName[name] !== undefined) return savedByName[name];
        return "";
    };

    if (trainingDetails.length > 0) {
        const grouped = {
            "सैद्धांतिक": [],
            "प्रात्यक्षिक": [],
            "प्रक्षेत्र": [],
        };

        trainingDetails.forEach((td) => {
            const sessionType = categoryToSession[td?.categoryType] || "सैद्धांतिक";
            grouped[sessionType].push({
                moduleId: td?.trainingModule_id || td?._id || "",
                moduleName: td?.trainingModuleName || "-",
                plannedSessions: resolvePlanned(td),
            });
        });

        return sessionOrder.map((sessionType) => ({
            sessionType,
            subject: savedSessionSubject[sessionType] ?? "",
            plannedSessions: savedSessionNumber[sessionType] ?? "",
            modules: grouped[sessionType] || [],
        }));
    }

    const hasSavedModules = sessionOrder.some(
        (st) => savedModulesBySession[st].length > 0
    );
    const hasSavedSessionInfo =
        Object.keys(savedSessionSubject).length > 0 ||
        Object.keys(savedSessionNumber).length > 0;

    if (!hasSavedModules && !hasSavedSessionInfo) return null;

    return sessionOrder.map((sessionType) => ({
        sessionType,
        subject: savedSessionSubject[sessionType] ?? "",
        plannedSessions: savedSessionNumber[sessionType] ?? "",
        modules: savedModulesBySession[sessionType] || [],
    }));
};

export const FormFillCombine = () => {
    const dispatch = useDispatch();

    const [isGeneratingReport, setIsGeneratingReport] = React.useState(false);
    const [reportSuccess, setReportSuccess] = React.useState("");

    const [studentPresentCount, setStudentPresentCount] = React.useState({
        ekunPat: "",
        vidyarthi: "",
        vidyarthini: "",
        ekunUpasthit: "",
    });

    const [categorywiseImage, setCategorywiseImage] = React.useState([]);
    const [selectedImages, setSelectedImages] = React.useState([]);

    // ---- Report prefill states ----
    const [reportInfo, setReportInfo] = React.useState(null);
    const [reportInfoLoader, setReportInfoLoader] = React.useState(false);
    const [reportInfoError, setReportInfoError] = React.useState("");
    const [isReportGenerated, setIsReportGenerated] = React.useState(false);

    // ---- Signature states ----
    const [signatureFile, setSignatureFile] = React.useState(null);
    const [signaturePreview, setSignaturePreview] = React.useState("");
    const [signatureUrl, setSignatureUrl] = React.useState("");
    const [signatureDimensions, setSignatureDimensions] = React.useState("");
    const [isUploadingSignature, setIsUploadingSignature] = React.useState(false);
    const [signatureError, setSignatureError] = React.useState("");
    const [signatureSuccess, setSignatureSuccess] = React.useState(false);

    const { state } = useLocation();

    React.useEffect(() => {
        if (state?.trainerSignature) {
            setSignatureUrl(state.trainerSignature);
        }
    }, [state?.trainerSignature]);

    const trainingDetails = state?.trainingDetails || [];

    const theoryModules = trainingDetails.filter(
        (t) => t?.categoryType === "Theory"
    );
    const practicalModules = trainingDetails.filter(
        (t) => t?.categoryType === "Practical"
    );

    // ---- Header values ----
    const buildHeaderValues = React.useCallback(
        (s) => ({
            shaala: `शासकीय आश्रम शाळा${s?.grampanchayatName ? `, ${s.grampanchayatName}` : ""}`,
            gaon: s?.grampanchayatName || "-",
            taluka: s?.talukaName || "-",
            jilha: s?.districtName || "-",
            prakalpKaryalay: "प्रकल्प कार्यालय 1",
            mahina: s?.month || "-",
            prashikshak: s?.trainerName || "-",
        }),
        []
    );

    const [headerValues, setHeaderValues] = React.useState(() =>
        buildHeaderValues(state)
    );

    React.useEffect(() => {
        if (reportInfo) return;
        setHeaderValues(buildHeaderValues(state));
    }, [
        buildHeaderValues,
        reportInfo,
        state?.grampanchayatName,
        state?.talukaName,
        state?.districtName,
        state?.month,
        state?.trainerName,
    ]);

    // ---- Session rows ----
    const [sessionSubjectRows, setSessionSubjectRows] = React.useState(() => [
        {
            sessionType: "सैद्धांतिक",
            subject: "",
            plannedSessions: "",
            modules: theoryModules.map((m) => ({
                moduleId: m?.trainingModule_id || m?._id || "",
                moduleName: m?.trainingModuleName || "-",
                plannedSessions: "",
            })),
        },
        {
            sessionType: "प्रात्यक्षिक",
            subject: "",
            plannedSessions: "",
            modules: practicalModules.map((m) => ({
                moduleId: m?.training_module_id || m?._id || "",
                moduleName: m?.trainingModuleName || "-",
                plannedSessions: "",
            })),
        },
        { sessionType: "प्रक्षेत्र", modules: [], subject: "", plannedSessions: "" },
    ]);

    // ---- Fetch prefill data ----
    const getReportInfo = async () => {
        if (!state?.trainingModule_id && !state?.grampanchayat_id) return;

        setReportInfoLoader(true);
        setReportInfoError("");

        try {
            const payload = {
                isMonthlyReport: true,
                trainingModule_id: state?.trainingModule_id || "",
                grampanchayat_id: state?.grampanchayat_id || "",
                trainerName: state?.trainerName || "",
                startDate: state?.fromDate ? state.fromDate.split("T")[0] : "",
            };

            const response = await fetch(
                BASE_URL + "/report/getreport/getReportDataByTrainerNew",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );

            const result = await response.json();
            

            setReportInfo(result);

            if (result && typeof result === "object" && !Array.isArray(result)) {
                const hasReportUrl =
                    Array.isArray(result?.reportURL) && result.reportURL.length > 0;
                const hasImageData =
                    Array.isArray(result?.imageData) && result.imageData.length > 0;
                const isCompleted = result?.reportStatus === "COMPLETED";

                setIsReportGenerated(hasReportUrl || hasImageData || isCompleted);

                const bd = {
                    ...(isPlainObject(result?.assignTrainerObject?.beneficiaryData)
                        ? result.assignTrainerObject.beneficiaryData
                        : {}),
                    ...(isPlainObject(result?.totalBeneficiaryData)
                        ? result.totalBeneficiaryData
                        : {}),
                    ...(isPlainObject(result?.beneficiaryData)
                        ? result.beneficiaryData
                        : {}),
                };

                if (Object.keys(bd).length > 0) {
                    setStudentPresentCount((prev) => ({
                        ...prev,
                        ekunPat: bd["एकूण पट"] ?? prev.ekunPat,
                        vidyarthi: bd["उपस्थित विद्यार्थी"] ?? prev.vidyarthi,
                        vidyarthini: bd["उपस्थित विद्यार्थिनी"] ?? prev.vidyarthini,
                        ekunUpasthit:
                            bd["एकूण उपस्थित"] !== undefined
                                ? String(bd["एकूण उपस्थित"])
                                : prev.ekunUpasthit,
                    }));

                    setHeaderValues((prev) => ({
                        shaala: bd["शाळा"] || prev.shaala || "शासकीय आश्रम शाळा",
                        gaon: bd["गाव"] || prev.gaon,
                        taluka: bd["तालुका"] || prev.taluka,
                        jilha: bd["जिल्हा"] || prev.jilha,
                        prakalpKaryalay:
                            bd["प्रकल्प कार्यालय"] ?? prev.prakalpKaryalay ?? "",
                        mahina: bd["महिना"] || prev.mahina,
                        prashikshak: bd["प्रशिक्षक"] || prev.prashikshak,
                    }));
                }

                const rows = buildSessionRowsFromReport(result);
                if (rows && rows.length > 0) {
                    setSessionSubjectRows(rows);
                }

                if (result?.trainerSignature) {
                    setSignatureUrl(result.trainerSignature);
                } else if (result?.assignTrainerObject?.trainerSignature) {
                    setSignatureUrl(result.assignTrainerObject.trainerSignature);
                } else if (state?.trainerSignature) {
                    setSignatureUrl(state.trainerSignature);
                }

                const imageSource =
                    (Array.isArray(result?.imageData) && result.imageData.length > 0
                        ? result.imageData
                        : null) ||
                    (Array.isArray(result?.assignTrainerObject?.imageData) &&
                        result.assignTrainerObject.imageData.length > 0
                        ? result.assignTrainerObject.imageData
                        : null);

                if (imageSource) {
                    setCategorywiseImage(
                        imageSource.map((obj) => ({
                            categoryName: obj?.categoryName || "",
                            images: Array.isArray(obj?.images) ? obj.images : [],
                            showUpload: false,
                        }))
                    );
                }
            }
        } catch (error) {
            console.error("Failed to fetch report info:", error);
            setReportInfoError("Failed to load report info");
            setReportInfo(null);
        } finally {
            setReportInfoLoader(false);
        }
    };

    const getAllCategories = async () => {
        if (!state?.grampanchayat_id && !state?._id) return;

        try {
            const response = await fetch(
                BASE_URL + "/trainerevidence/trainerevidence/grampanchayat",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        grampanchayat_id: [state?.grampanchayat_id],
                        trainingModule_id:
                            state?.trainingDetails?.map((item) => item?.trainingModule_id) || [],
                        training_id:
                            state?.trainingDetails?.map((item) => item?._id) || [],
                        isReport: true,
                    }),
                }
            );

            const result = await response.json();

            if (result && result !== "No data found") {
                const mapped = (Array.isArray(result) ? result : []).map((obj) => ({
                    categoryName: obj?._id,
                    images: obj?.category?.map((oo) => oo.documentUrl) ?? [],
                }));
                setCategorywiseImage((prev) =>
                    prev && prev.length > 0 ? prev : mapped
                );
            }
        } catch (error) {
            console.error("Failed to load categories:", error);
        }
    };

    // ---- Signature select handler ----
    const handleSignatureChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setSignatureError("");
        setSignatureSuccess(false);

        if (!ALLOWED_SIGNATURE_TYPES.includes(file.type)) {
            const msg = "Only PNG, JPG, JPEG or WEBP images are allowed.";
            setSignatureError(msg);
            alert(msg);
            e.target.value = "";
            return;
        }

        const maxBytes = MAX_SIGNATURE_SIZE_MB * 1024 * 1024;
        if (file.size > maxBytes) {
            const msg = `Signature file must be under ${MAX_SIGNATURE_SIZE_MB} MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)} MB.`;
            setSignatureError(msg);
            alert(msg);
            e.target.value = "";
            return;
        }

        if (file.size === 0) {
            const msg = "Selected file is empty. Please choose a valid image.";
            setSignatureError(msg);
            alert(msg);
            e.target.value = "";
            return;
        }

        let dimensions = null;
        try {
            dimensions = await inspectSignatureImage(file);
        } catch (err) {
            const msg = "Could not read the selected image. Please try another file.";
            setSignatureError(msg);
            alert(msg);
            e.target.value = "";
            return;
        }

        if (dimensions.error) {
            setSignatureError(dimensions.error);
            alert(dimensions.error);
            e.target.value = "";
            setSignatureFile(null);
            setSignaturePreview("");
            setSignatureDimensions("");
            return;
        }

        setSignatureDimensions(`${dimensions.width}×${dimensions.height}px`);
        setSignatureFile(file);
        setSignaturePreview(URL.createObjectURL(file));

        setIsUploadingSignature(true);
        try {
            const uploaded = await uploadSignatureToServer(file, state?._id);
            if (typeof uploaded === "string" && uploaded.startsWith("http")) {
                setSignatureUrl(uploaded);
                setSignatureSuccess(true);
            } else {
                setSignatureError("Upload did not return a valid URL");
                console.warn("Signature upload response:", uploaded);
            }
        } catch (err) {
            console.error("Signature upload failed:", err);
            setSignatureError("Signature upload failed. Please try again.");
        } finally {
            setIsUploadingSignature(false);
        }
    };

    React.useEffect(() => {
        return () => {
            if (signaturePreview) URL.revokeObjectURL(signaturePreview);
        };
    }, [signaturePreview]);

    const resolveSessionSubject = (type) => {
        const row = sessionSubjectRows.find((r) => r.sessionType === type);
        if (!row) return "-";
        if (row.modules?.length > 0) {
            const names = row.modules
                .map((m) => m.moduleName)
                .filter((n) => n && n !== "-");
            return names.length > 0 ? names.join(", ") : "-";
        }
        return (row.subject || "").trim() || "-";
    };

    const handleGenerateReport = async () => {
        if (isGeneratingReport) return;

        if (!signatureUrl) {
            setSignatureError(
                "Please upload the trainer's signature before generating the report."
            );
            document
                .getElementById("signature-section")
                ?.scrollIntoView({ behavior: "smooth" });
            return;
        }

        setIsGeneratingReport(true);
        setReportSuccess("");

        try {
            const presentCount =
                (Number(studentPresentCount.vidyarthi) || 0) +
                (Number(studentPresentCount.vidyarthini) || 0);
            const totalPat = Number(studentPresentCount.ekunPat) || 0;
            const attendancePercentage =
                totalPat > 0 ? `${((presentCount / totalPat) * 100).toFixed(1)}%` : "";

            const baseBd = {
                ...(isPlainObject(reportInfo?.assignTrainerObject?.beneficiaryData)
                    ? reportInfo.assignTrainerObject.beneficiaryData
                    : {}),
                ...(isPlainObject(reportInfo?.totalBeneficiaryData)
                    ? reportInfo.totalBeneficiaryData
                    : {}),
                ...(isPlainObject(reportInfo?.beneficiaryData)
                    ? reportInfo.beneficiaryData
                    : {}),
            };

            const imageData = categorywiseImage.reduce(
                (selectedCategories, { categoryName, images }) => {
                    const selectedCategoryImages = images.filter((imageItem) =>
                        selectedImages.some(
                            (selectedImage) =>
                                selectedImage.categoryName === categoryName &&
                                selectedImage.image === imageItem
                        )
                    );

                    if (selectedCategoryImages.length) {
                        selectedCategories.push({
                            categoryName,
                            images: selectedCategoryImages,
                        });
                    }

                    return selectedCategories;
                },
                []
            );

            const finalImageData =
                imageData.length > 0
                    ? imageData
                    : categorywiseImage.map((c) => ({
                        categoryName: c.categoryName,
                        images: c.images,
                    }));

            // ✅ 1. Cleaned beneficiaryData with imageData inside it
            const beneficiaryData = {
                ...baseBd,
                "एकूण पट": studentPresentCount.ekunPat,
                "उपस्थित विद्यार्थी": studentPresentCount.vidyarthi,
                "उपस्थित विद्यार्थिनी": studentPresentCount.vidyarthini,
                "एकूण उपस्थित": attendancePercentage,
                "एकूण उपस्थित संख्या": presentCount,
                "उपस्थिती टक्केवारी": attendancePercentage,
                "शाळा": headerValues.shaala || "-",
                "गाव": headerValues.gaon || "-",
                "तालुका": headerValues.taluka || "-",
                "जिल्हा": headerValues.jilha || "-",
                "प्रकल्प कार्यालय": headerValues.prakalpKaryalay || "-",
                "महिना": headerValues.mahina || "-",
                "प्रशिक्षक": headerValues.prashikshak || "-",
                "सैद्धांतिक": resolveSessionSubject("सैद्धांतिक"),
                "प्रात्यक्षिक": resolveSessionSubject("प्रात्यक्षिक"),
                "प्रक्षेत्र": resolveSessionSubject("प्रक्षेत्र"),
                "तपशील": sessionSubjectRows.map(
                    ({ sessionType, modules, subject, plannedSessions }) => ({
                        sessionType,
                        subject: subject ?? "",
                        plannedSessions: plannedSessions ?? "",
                        modules: (modules || []).map((m) => ({
                            moduleId: m.moduleId,
                            moduleName: m.moduleName,
                            plannedSessions: m.plannedSessions ?? "",
                        })),
                    })
                ),
                categoryType: baseBd?.categoryType || state?.categoryType,
                imageData: finalImageData, // ✅ Added inside beneficiaryData
            };

            // ✅ 2. Cleaned root payload (Removed totalBeneficiaryData & imageData from root)
            const data = {
                isMonthlyReport: true,
                trainer_idAll:
                    reportInfo?.assignTrainerObject?.trainer_idAll ||
                    state?.trainer_idAll,
                trainerNameAll:
                    reportInfo?.assignTrainerObject?.trainerNameAll ||
                    state?.trainerNameAll,
                trainerMobileAll:
                    reportInfo?.assignTrainerObject?.trainerMobileAll ||
                    state?.trainerMobileAll,
                trainerSignature: signatureUrl || state?.trainerSignature || "",
                trainerCount:
                    reportInfo?.assignTrainerObject?.trainerCount ||
                    state?.trainerCount,
                docUploadedCount:
                    reportInfo?.assignTrainerObject?.docUploadedCount ||
                    state?.docUploadedCount,
                docApprovedCount:
                    reportInfo?.assignTrainerObject?.docApprovedCount ||
                    state?.docApprovedCount,
                typeOfDoc: state?.typeOfDoc,
                typeOfDocStatus: state?.typeOfDocStatus,
                isAssigned: state?.isAssigned,
                isApprovedFinal: state?.isApprovedFinal,
                isRejectedFinal: state?.isRejectedFinal,
                castBifircation: state?.castBifircation,
                representative: state?.representative,
                feedbackformData: state?.feedbackformData,
                resultform: state?.resultform,
                status: state?.status,
                isDelete: state?.isDelete,
                _id: reportInfo?._id || state?._id,
                trainer_id:
                    reportInfo?.assignTrainerObject?.trainer_id || state?.trainer_id,
                trainerName: reportInfo?.trainerName || state?.trainerName,
                trainingModule_id:
                    reportInfo?.trainingModuleId || state?.trainingModule_id,
                trainingModuleName:
                    reportInfo?.trainingModuleName || state?.trainingModuleName,
                distric_id: state?.distric_id,
                districtName:
                    reportInfo?.districtName || state?.districtName,
                taluka_id: reportInfo?.talukaId || state?.taluka_id,
                talukaName: reportInfo?.talukaName || state?.talukaName,
                grampanchayat_id:
                    reportInfo?.grampanchayatId || state?.grampanchayat_id,
                grampanchayatName:
                    reportInfo?.grampanchayatName || state?.grampanchayatName,
                createdBy: state?.createdBy,
                createdAt: state?.createdAt,
                updatedAt: state?.updatedAt,
                endDate: reportInfo?.endDate || state?.endDate,
                shift: state?.shift,
                startDate: reportInfo?.startDate || state?.startDate,
                updatedBy: state?.updatedBy,
                month: state?.month,
                trainingDetails: state?.trainingDetails,
                formHeader: headerValues,
                beneficiaryData, // ✅ imageData is now inside this object
            };

            const res = await dispatch(Action.GenerRateReport(data));

            const pdfUrl = typeof res === "string" ? res : res?.pdfUrl;

            if (!pdfUrl) {
                throw new Error(
                    res?.message || "Report generation did not return a PDF URL."
                );
            }

            const link = document.createElement("a");
            link.href = pdfUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            link.remove();

            setIsReportGenerated(true);
            setReportSuccess("धन्यवाद! अहवाल यशस्वीरित्या तयार झाला.");
            getReportInfo();
        } catch (error) {
            console.error("Failed to generate or download report:", error);
        } finally {
            // getReportInfo();
            setIsGeneratingReport(false);
        }
    };

    React.useEffect(() => {
        getAllCategories();
        // eslint-disable-next-line
    }, [state?._id]);

    React.useEffect(() => {
        getReportInfo();
        // eslint-disable-next-line
    }, [
        state?.trainingModule_id,
        state?.grampanchayat_id,
        state?.trainerName,
    ]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: { xs: 2, sm: 2.5 },
                minHeight: "100vh",
                width: "100%",
                minWidth: 0,
                overflowX: "hidden",
                paddingBottom: { xs: 3, sm: 4 },
                boxSizing: "border-box",
            }}
        >
            <Typography
                sx={{
                    width: "100%",
                    margin: { xs: "12px 0 4px", sm: "20px 0 8px" },
                    fontSize: { xs: 24, sm: 32, md: 40 },
                    fontWeight: 700,
                    textAlign: "center",
                }}
            >
                प्रशिक्षण अहवाल
            </Typography>

            {reportInfoLoader && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography>Loading report info...</Typography>
                </Box>
            )}
            {reportInfoError && (
                <Typography sx={{ color: "red" }}>{reportInfoError}</Typography>
            )}

            <TrainingFormHeader
                values={headerValues}
                editable={true}
                editableFields={["shaala", "prakalpKaryalay"]}
                onChange={(field, val) =>
                    setHeaderValues((prev) => ({ ...prev, [field]: val }))
                }
            />

            <SessionSubjectDetails
                rows={sessionSubjectRows}
                onModulePlannedSessionsChange={(rowIndex, moduleIndex, value) =>
                    setSessionSubjectRows((rows) =>
                        rows.map((row, rIdx) =>
                            rIdx === rowIndex
                                ? {
                                    ...row,
                                    modules: row.modules.map((mod, mIdx) =>
                                        mIdx === moduleIndex
                                            ? { ...mod, plannedSessions: value }
                                            : mod
                                    ),
                                }
                                : row
                        )
                    )
                }
                onEmptyRowChange={(rowIndex, field, value) =>
                    setSessionSubjectRows((rows) =>
                        rows.map((row, rIdx) =>
                            rIdx === rowIndex ? { ...row, [field]: value } : row
                        )
                    )
                }
            />

            <StudentAttendanceCount
                values={studentPresentCount}
                onChange={(field, val) =>
                    setStudentPresentCount((prev) => ({ ...prev, [field]: val }))
                }
            />

            <Box
                sx={{
                    width: "100%",
                    maxWidth: 1024,
                    display: "flex",
                    boxSizing: "border-box",
                    flexDirection: "column",
                    border: "2px solid",
                    borderRadius: 2,
                    padding: { xs: 1, sm: 2.5 },
                    gap: { xs: 1.5, sm: 2.5 },
                    minWidth: 0,
                }}
            >
                {categorywiseImage
                    .filter((item) => !videoCategories.includes(item.categoryName))
                    .map(({ images, categoryName }) => (
                        <Box
                            key={categoryName}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                border: "2px solid",
                                borderRadius: 2,
                                gap: { xs: 1.5, sm: 2.5 },
                                padding: { xs: 1, sm: 2.5 },
                                minWidth: 0,
                            }}
                        >
                            <Typography
                                sx={{
                                    margin: { xs: "4px 0", sm: "8px 12px" },
                                    fontSize: { xs: 18, sm: 20 },
                                    fontWeight: 600,
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {categoryName}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                    gap: { xs: 1, sm: 2.5 },
                                    minWidth: 0,
                                }}
                            >
                                {images.map((img, index2) => {
                                    const isSelected = selectedImages.some(
                                        (selectedImage) =>
                                            selectedImage.categoryName === categoryName &&
                                            selectedImage.image === img
                                    );

                                    return (
                                        <Box
                                            key={`${categoryName}-${index2}`}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                maxWidth: "100%",
                                            }}
                                        >
                                            <img
                                                src={img}
                                                alt={`${categoryName}-${index2}`}
                                                style={{
                                                    maxWidth: 160,
                                                    maxHeight: 160,
                                                    objectFit: "contain",
                                                    border: "1px solid #ccc",
                                                    borderRadius: 4,
                                                    padding: 2,
                                                    background: "#fff",
                                                }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() =>
                                                            setSelectedImages((prev) =>
                                                                isSelected
                                                                    ? prev.filter(
                                                                        (selectedImage) =>
                                                                            selectedImage.categoryName !== categoryName ||
                                                                            selectedImage.image !== img
                                                                    )
                                                                    : [...prev, { categoryName, image: img }]
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Choose"
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
            </Box>

            <Box
                id="signature-section"
                sx={{
                    width: "100%",
                    maxWidth: 1024,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 1,
                }}
            >
                <Typography sx={{ fontWeight: 700, textAlign: "left" }}>
                    प्रशिक्षकांची स्वाक्षरी - {state?.trainerName || "-"}
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        disabled={isUploadingSignature}
                        sx={{ textTransform: "none" }}
                    >
                        {isUploadingSignature
                            ? "Uploading..."
                            : signatureFile
                                ? "Change Signature"
                                : "Upload Signature"}
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            hidden
                            onChange={handleSignatureChange}
                        />
                    </Button>

                    {isUploadingSignature && <CircularProgress size={20} />}

                    {signaturePreview && (
                        <Box
                            sx={{
                                border: "2px solid #1976d2",
                                borderRadius: 1,
                                padding: 0.5,
                                width: SIGNATURE_STANDARD_WIDTH,
                                height: SIGNATURE_STANDARD_HEIGHT,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fff",
                                overflow: "hidden",
                            }}
                        >
                            <img
                                src={signaturePreview}
                                alt="signature preview"
                                style={{
                                    display: "block",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                    )}

                    {!signaturePreview && signatureUrl && (
                        <Box
                            sx={{
                                border: "2px solid #1976d2",
                                borderRadius: 1,
                                padding: 0.5,
                                width: SIGNATURE_STANDARD_WIDTH,
                                height: SIGNATURE_STANDARD_HEIGHT,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fff",
                                overflow: "hidden",
                            }}
                        >
                            <img
                                src={signatureUrl}
                                alt="trainer signature"
                                style={{
                                    display: "block",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                    )}
                </Box>

                <Typography variant="caption" sx={{ color: "#666" }}>
                    Allowed: PNG / JPG / JPEG / WEBP · Max {MAX_SIGNATURE_SIZE_MB} MB
                    <br />
                    Standard dimensions: {SIGNATURE_STANDARD_WIDTH}×{SIGNATURE_STANDARD_HEIGHT}px (3:1 ratio).
                    Allowed range: {SIGNATURE_MIN_WIDTH}×{SIGNATURE_MIN_HEIGHT}px to {SIGNATURE_MAX_WIDTH}×{SIGNATURE_MAX_HEIGHT}px.
                </Typography>

                {signatureDimensions && !signatureError && (
                    <Typography variant="caption" sx={{ color: EDITABLE_ICON_COLOR }}>
                        Selected image size: {signatureDimensions}
                    </Typography>
                )}

                {signatureUrl && !signatureError && (
                    <Typography variant="caption" sx={{ color: "green" }}>
                        ✓ Signature uploaded
                    </Typography>
                )}

                {signatureSuccess && (
                    <Typography variant="caption" sx={{ color: "green", fontWeight: 600 }}>
                        धन्यवाद! स्वाक्षरी यशस्वीरित्या अपलोड झाली.
                    </Typography>
                )}

                {signatureError && (
                    <Typography variant="caption" sx={{ color: "red" }}>
                        {signatureError}
                    </Typography>
                )}
            </Box>

            <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={isGeneratingReport || isUploadingSignature}
                startIcon={
                    isGeneratingReport ? <CircularProgress size={18} color="inherit" /> : null
                }
                sx={{
                    backgroundColor: "#1d5a96",
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: 600,
                    padding: "10px 24px",
                    borderRadius: "6px",

                    "&:hover": {
                        backgroundColor: "#154675",
                    },
                }}
            >
                {isGeneratingReport
                    ? "Generating..."
                    : isReportGenerated
                        ? "Regenerate Report"
                        : "Generate Report"}
            </Button>

            {reportSuccess && (
                <Typography sx={{ color: "green", fontWeight: 600 }}>
                    {reportSuccess}
                </Typography>
            )}
        </Box>
    );
};


const TrainingFormHeader = ({
    values = {},
    editable = false,
    editableFields = null,
    onChange = null,
}) => {
    const isFieldEditable = (fieldName) => {
        if (editableFields && editableFields.length > 0) {
            return editableFields.includes(fieldName);
        }
        return editable;
    };

    return (
        <Box
            sx={{
                border: borderSx,
                borderRight: "0px",
                borderColor: "text.primary",
                width: "100%",
                maxWidth: 1024,
                minWidth: 0,
                fontFamily: '"Noto Sans Devanagari", "Mangal", Arial, sans-serif',
            }}
        >
            <FormRow>
                <FormCell
                    label="शाळा"
                    name="shaala"
                    value={values.shaala}
                    editable={isFieldEditable("shaala")}
                    onChange={onChange}
                />
            </FormRow>

            <FormRow>
                <FormCell
                    label="गाव"
                    name="gaon"
                    value={values.gaon}
                    editable={isFieldEditable("gaon")}
                    onChange={onChange}
                />
                <FormCell
                    label="तालुका"
                    name="taluka"
                    value={values.taluka}
                    editable={isFieldEditable("taluka")}
                    onChange={onChange}
                />
            </FormRow>

            <FormRow>
                <FormCell
                    label="जिल्हा"
                    name="jilha"
                    value={values.jilha}
                    editable={isFieldEditable("jilha")}
                    onChange={onChange}
                    labelWidth={110}
                />
                <FormCell
                    label="प्रकल्प कार्यालय"
                    name="prakalpKaryalay"
                    value={values.prakalpKaryalay}
                    editable={isFieldEditable("prakalpKaryalay")}
                    onChange={onChange}
                    labelWidth={150}
                />
            </FormRow>

            <FormRow isLast>
                <FormCell
                    label="महिना"
                    name="mahina"
                    value={values.mahina}
                    editable={isFieldEditable("mahina")}
                    onChange={onChange}
                />
                <FormCell
                    label="प्रशिक्षक"
                    name="prashikshak"
                    value={values.prashikshak}
                    editable={isFieldEditable("prashikshak")}
                    onChange={onChange}
                    labelWidth={150}
                />
            </FormRow>
        </Box>
    );
};


const FormCell = ({
    label,
    name,
    value = "",
    onChange = null,
    editable = false,
    labelWidth = 110,
}) => {
    const handleChange = (e) => {
        if (onChange) onChange(name, e.target.value);
    };

    return (
        <Box className="form-cell" sx={{ display: "flex", flex: 1, minWidth: 0 }}>
            <Box
                sx={{
                    width: { xs: Math.min(labelWidth, 120), md: labelWidth },
                    maxWidth: { xs: "45%", md: "none" },
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    px: { xs: 1, sm: 1.5 },
                    py: 1,
                    fontWeight: 700,
                    fontSize: { xs: "0.8rem", sm: "0.95rem" },
                    borderRight: borderSx,
                    borderColor: "text.primary",
                    overflowWrap: "anywhere",
                }}
            >
                {label}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    px: { xs: 1, sm: 1.5 },
                    py: 0.5,
                    minWidth: 0,
                    borderRight: borderSx,
                    borderColor: "text.primary",
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        borderRadius: "4px",
                        backgroundColor: editable ? EDITABLE_BG : "transparent",
                        ...(editable && {
                            border: `1.5px dashed ${EDITABLE_BORDER_COLOR}`,
                            padding: "2px 8px",
                            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                                borderColor: EDITABLE_HOVER_BORDER,
                            },
                            "&:focus-within": {
                                borderColor: EDITABLE_FOCUS_BORDER,
                                borderStyle: "solid",
                                boxShadow: `0 0 0 2px rgba(25, 118, 210, 0.18)`,
                            },
                        }),
                    }}
                >
                    {editable && (
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                top: "-9px",
                                right: "6px",
                                fontSize: "0.7rem",
                                lineHeight: 1,
                                color: EDITABLE_ICON_COLOR,
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                px: 0.4,
                                py: 0.15,
                                border: `1px solid ${EDITABLE_BORDER_COLOR}`,
                                pointerEvents: "none",
                            }}
                        >
                            ✎
                        </Box>
                    )}
                    <TextField
                        name={name}
                        value={value ?? ""}
                        onChange={handleChange}
                        variant="standard"
                        fullWidth
                        placeholder={editable ? EDITABLE_PLACEHOLDER : undefined}
                        InputProps={{
                            readOnly: !editable,
                            disableUnderline: true,
                        }}
                        sx={{
                            "& .MuiInputBase-input": {
                                fontSize: "0.95rem",
                                cursor: editable ? "text" : "default",
                                padding: "4px 0",
                            },
                            "& .MuiInputBase-input::placeholder": {
                                color: "#6b8cae",
                                fontStyle: "italic",
                                opacity: 1,
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const FormRow = ({ children, isLast = false }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            borderBottom: isLast ? "none" : borderSx,
            borderColor: "text.primary",
            "& > .form-cell + .form-cell": {
                borderTop: { xs: borderSx, md: "none" },
            },
        }}
    >
        {children}
    </Box>
);


const DEVANAGARI_DIGITS = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
const toDevanagariNumeral = (num) =>
    String(num)
        .split("")
        .map((d) => DEVANAGARI_DIGITS[Number(d)] ?? d)
        .join("");

const DEFAULT_ROWS = [
    { sessionType: "सैद्धांतिक", modules: [], subject: "", plannedSessions: "" },
    { sessionType: "प्रात्यक्षिक", modules: [], subject: "", plannedSessions: "" },
    { sessionType: "प्रक्षेत्र", modules: [], subject: "", plannedSessions: "" },
];

const cellBorderSx = {
    border: borderSx,
    borderColor: "text.primary",
};

const onlyDigits = (value) => /^\d*$/.test(value);

const SessionSubjectDetails = ({
    rows = DEFAULT_ROWS,
    title = "सत्र विषय तपशील",
    minRowHeight = 90,
    onModulePlannedSessionsChange = null,
    onEmptyRowChange = null,
}) => {
    return (
        <TableContainer
            sx={{
                width: "100%",
                maxWidth: 1024,
                overflowX: "auto",
                fontFamily: '"Noto Sans Devanagari", "Mangal", Arial, sans-serif',
            }}
        >
            <Table
                sx={{
                    minWidth: { xs: 720, md: "100%" },
                    borderCollapse: "collapse",
                    border: borderSx,
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell
                            colSpan={4}
                            align="center"
                            sx={{
                                ...cellBorderSx,
                                fontWeight: 700,
                                fontSize: "1rem",
                                borderBottom: borderSx,
                            }}
                        >
                            {title}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell
                            align="center"
                            sx={{ ...cellBorderSx, fontWeight: 700, width: 90 }}
                        >
                            सत्र क्र.
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{ ...cellBorderSx, fontWeight: 700, width: 160 }}
                        >
                            सत्र स्वरूप
                        </TableCell>
                        <TableCell align="center" sx={{ ...cellBorderSx, fontWeight: 700 }}>
                            विषय
                        </TableCell>
                        <TableCell align="center" sx={{ ...cellBorderSx, fontWeight: 700 }}>
                            नियोजित सत्रांची संख्या
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, index) => {
                        const moduleCount = row.modules?.length || 0;

                        if (moduleCount === 0) {
                            const isPrakshetra = row.sessionType === "प्रक्षेत्र";

                            return (
                                <TableRow key={index}>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            ...cellBorderSx,
                                            verticalAlign: "middle",
                                            height: minRowHeight,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {toDevanagariNumeral(index + 1)}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            ...cellBorderSx,
                                            verticalAlign: "middle",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {row.sessionType}
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            ...cellBorderSx,
                                            verticalAlign: "middle",
                                            p: 1,
                                        }}
                                    >
                                        <Box sx={{ position: "relative" }}>
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    position: "absolute",
                                                    top: "-8px",
                                                    right: "10px",
                                                    fontSize: "0.7rem",
                                                    lineHeight: 1,
                                                    color: EDITABLE_ICON_COLOR,
                                                    backgroundColor: "#fff",
                                                    borderRadius: "50%",
                                                    px: 0.4,
                                                    py: 0.15,
                                                    border: `1px solid ${EDITABLE_BORDER_COLOR}`,
                                                    zIndex: 2,
                                                    pointerEvents: "none",
                                                }}
                                            >
                                                ✎
                                            </Box>
                                            <TextField
                                                value={row.subject ?? ""}
                                                onChange={(e) =>
                                                    onEmptyRowChange?.(
                                                        index,
                                                        "subject",
                                                        e.target.value
                                                    )
                                                }
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                placeholder={EDITABLE_PLACEHOLDER}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        backgroundColor: EDITABLE_BG,
                                                        "& fieldset": {
                                                            borderColor: EDITABLE_BORDER_COLOR,
                                                            borderWidth: "1.5px",
                                                            borderStyle: "dashed",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: EDITABLE_HOVER_BORDER,
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: EDITABLE_FOCUS_BORDER,
                                                            borderWidth: "2px",
                                                            borderStyle: "solid",
                                                            boxShadow: `0 0 0 2px rgba(25, 118, 210, 0.15)`,
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.9rem",
                                                        whiteSpace: "pre-wrap",
                                                    },
                                                    "& .MuiInputBase-input::placeholder": {
                                                        color: "#6b8cae",
                                                        fontStyle: "italic",
                                                        opacity: 1,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            ...cellBorderSx,
                                            verticalAlign: "middle",
                                            p: 1,
                                            textAlign: "center",
                                        }}
                                    >
                                        {isPrakshetra ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "#999" }}
                                            >
                                                —
                                            </Typography>
                                        ) : (
                                            <Box sx={{ position: "relative" }}>
                                                <Box
                                                    aria-hidden
                                                    sx={{
                                                        position: "absolute",
                                                        top: "-8px",
                                                        right: "10px",
                                                        fontSize: "0.7rem",
                                                        lineHeight: 1,
                                                        color: EDITABLE_ICON_COLOR,
                                                        backgroundColor: "#fff",
                                                        borderRadius: "50%",
                                                        px: 0.4,
                                                        py: 0.15,
                                                        border: `1px solid ${EDITABLE_BORDER_COLOR}`,
                                                        zIndex: 2,
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    ✎
                                                </Box>
                                                <TextField
                                                    value={row.plannedSessions ?? ""}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (!onlyDigits(val)) return;
                                                        onEmptyRowChange?.(
                                                            index,
                                                            "plannedSessions",
                                                            val
                                                        );
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    placeholder="संख्या"
                                                    inputProps={{
                                                        inputMode: "numeric",
                                                        style: { textAlign: "center" },
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            backgroundColor: EDITABLE_BG,
                                                            "& fieldset": {
                                                                borderColor:
                                                                    EDITABLE_BORDER_COLOR,
                                                                borderWidth: "1.5px",
                                                                borderStyle: "dashed",
                                                            },
                                                            "&:hover fieldset": {
                                                                borderColor:
                                                                    EDITABLE_HOVER_BORDER,
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                borderColor:
                                                                    EDITABLE_FOCUS_BORDER,
                                                                borderWidth: "2px",
                                                                borderStyle: "solid",
                                                                boxShadow: `0 0 0 2px rgba(25, 118, 210, 0.15)`,
                                                            },
                                                        },
                                                        "& .MuiInputBase-input": {
                                                            fontSize: "0.9rem",
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        }

                        return row.modules.map((mod, mIdx) => (
                            <TableRow key={`${index}-${mIdx}`}>
                                {mIdx === 0 && (
                                    <>
                                        <TableCell
                                            align="center"
                                            rowSpan={moduleCount}
                                            sx={{
                                                ...cellBorderSx,
                                                verticalAlign: "middle",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {toDevanagariNumeral(index + 1)}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            rowSpan={moduleCount}
                                            sx={{
                                                ...cellBorderSx,
                                                verticalAlign: "middle",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {row.sessionType}
                                        </TableCell>
                                    </>
                                )}

                                <TableCell sx={{ ...cellBorderSx, verticalAlign: "top", p: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "0.95rem",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {mod.moduleName}
                                    </Typography>
                                </TableCell>

                                <TableCell sx={{ ...cellBorderSx, verticalAlign: "top", p: 1 }}>
                                    <Box sx={{ position: "relative" }}>
                                        <Box
                                            aria-hidden
                                            sx={{
                                                position: "absolute",
                                                top: "-8px",
                                                right: "10px",
                                                fontSize: "0.7rem",
                                                lineHeight: 1,
                                                color: EDITABLE_ICON_COLOR,
                                                backgroundColor: "#fff",
                                                borderRadius: "50%",
                                                px: 0.4,
                                                py: 0.15,
                                                border: `1px solid ${EDITABLE_BORDER_COLOR}`,
                                                zIndex: 2,
                                                pointerEvents: "none",
                                            }}
                                        >
                                            ✎
                                        </Box>
                                        <TextField
                                            value={mod.plannedSessions ?? ""}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (!onlyDigits(val)) return;
                                                onModulePlannedSessionsChange?.(
                                                    index,
                                                    mIdx,
                                                    val
                                                );
                                            }}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            placeholder="संख्या"
                                            inputProps={{
                                                inputMode: "numeric",
                                                style: { textAlign: "center" },
                                                "aria-label": `नियोजित सत्रांची संख्या for ${mod.moduleName}`,
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: EDITABLE_BG,
                                                    "& fieldset": {
                                                        borderColor: EDITABLE_BORDER_COLOR,
                                                        borderWidth: "1.5px",
                                                        borderStyle: "dashed",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: EDITABLE_HOVER_BORDER,
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: EDITABLE_FOCUS_BORDER,
                                                        borderWidth: "2px",
                                                        borderStyle: "solid",
                                                        boxShadow: `0 0 0 2px rgba(25, 118, 210, 0.15)`,
                                                    },
                                                },
                                                "& .MuiInputBase-input": {
                                                    fontSize: "0.9rem",
                                                },
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ));
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};


let DEFAULT_VALUES = {
    ekunPat: "",
    vidyarthi: "",
    vidyarthini: "",
    ekunUpasthit: "",
};

const StudentAttendanceCount = ({
    values = DEFAULT_VALUES,
    title = "उपस्थित विद्यार्थी संख्या",
    editable = true,
    autoCalculateTotal = true,
    onChange = null,
}) => {
    const emitChange = (fieldName, newValue) => {
        if (onChange) onChange(fieldName, newValue);
    };

    const handleFieldChange = (fieldName) => (e) => {
        const newValue = e.target.value;

        if (!/^\d*$/.test(newValue)) {
            return;
        }

        const ekunPat = fieldName === "ekunPat" ? newValue : values.ekunPat;
        const vidyarthi = fieldName === "vidyarthi" ? newValue : values.vidyarthi;
        const vidyarthini = fieldName === "vidyarthini" ? newValue : values.vidyarthini;
        const presentCount = (Number(vidyarthi) || 0) + (Number(vidyarthini) || 0);

        if (ekunPat !== "" && presentCount > Number(ekunPat)) {
            return;
        }

        emitChange(fieldName, newValue);

        if (autoCalculateTotal && ["ekunPat", "vidyarthi", "vidyarthini"].includes(fieldName)) {
            const numVidyarthi = Number(vidyarthi) || 0;
            const numVidyarthini = Number(vidyarthini) || 0;
            const total = numVidyarthi + numVidyarthini;
            const pat = Number(ekunPat) || 0;

            const percentStr =
                pat > 0 && (vidyarthi !== "" || vidyarthini !== "")
                    ? `${((total / pat) * 100).toFixed(1)}%`
                    : "";

            emitChange("ekunUpasthit", percentStr);
        }
    };

    const columns = [
        { key: "ekunPat", label: "एकूण पट" },
        { key: "vidyarthi", label: "उपस्थित विद्यार्थी" },
        { key: "vidyarthini", label: "उपस्थित विद्यार्थिनी" },
        { key: "ekunUpasthit", label: "एकूण उपस्थित" },
    ];

    const totalPat = Number(values.ekunPat) || 0;
    const presentCount =
        (Number(values.vidyarthi) || 0) + (Number(values.vidyarthini) || 0);
    const percentage =
        totalPat > 0 ? `${((presentCount / totalPat) * 100).toFixed(1)}%` : null;

    return (
        <TableContainer
            sx={{
                width: "100%",
                maxWidth: 1024,
                overflowX: "auto",
                fontFamily: '"Noto Sans Devanagari", "Mangal", Arial, sans-serif',
            }}
        >
            <Table sx={{ minWidth: { xs: 560, md: "100%" }, borderCollapse: "collapse" }}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            colSpan={columns.length}
                            align="center"
                            sx={{ ...cellBorderSx, fontWeight: 700, fontSize: "1rem" }}
                        >
                            {title}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        {columns.map((col) => (
                            <TableCell
                                key={col.key}
                                align="center"
                                sx={{ ...cellBorderSx, fontWeight: 700 }}
                            >
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    <TableRow>
                        {columns.map((col) => {
                            const isTotalField = col.key === "ekunUpasthit";
                            const fieldIsEditable =
                                editable && !(isTotalField && autoCalculateTotal);

                            if (isTotalField) {
                                return (
                                    <TableCell
                                        key={col.key}
                                        align="center"
                                        sx={{ ...cellBorderSx, height: 56, p: 1 }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            <Typography
                                                sx={{ fontWeight: 700, fontSize: "1rem" }}
                                            >
                                                {percentage ?? "—"}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "#666" }}
                                            >
                                                {presentCount} / {totalPat || 0}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                );
                            }

                            return (
                                <TableCell
                                    key={col.key}
                                    align="center"
                                    sx={{ ...cellBorderSx, height: 56, p: 1 }}
                                >
                                    {fieldIsEditable ? (
                                        <Box sx={{ position: "relative" }}>
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    position: "absolute",
                                                    top: "-8px",
                                                    right: "6px",
                                                    fontSize: "0.65rem",
                                                    lineHeight: 1,
                                                    color: EDITABLE_ICON_COLOR,
                                                    backgroundColor: "#fff",
                                                    borderRadius: "50%",
                                                    px: 0.35,
                                                    py: 0.1,
                                                    border: `1px solid ${EDITABLE_BORDER_COLOR}`,
                                                    zIndex: 2,
                                                    pointerEvents: "none",
                                                }}
                                            >
                                                ✎
                                            </Box>
                                            <TextField
                                                value={values[col.key] ?? ""}
                                                onChange={handleFieldChange(col.key)}
                                                variant="outlined"
                                                size="small"
                                                type="text"
                                                placeholder="संख्या"
                                                aria-label={`${col.label} संख्या`}
                                                inputProps={{
                                                    inputMode: "numeric",
                                                    min: 0,
                                                    style: { textAlign: "center" },
                                                }}
                                                fullWidth
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        backgroundColor: EDITABLE_BG,
                                                        "& fieldset": {
                                                            borderColor: EDITABLE_BORDER_COLOR,
                                                            borderWidth: "1.5px",
                                                            borderStyle: "dashed",
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: EDITABLE_HOVER_BORDER,
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: EDITABLE_FOCUS_BORDER,
                                                            borderWidth: "2px",
                                                            borderStyle: "solid",
                                                            boxShadow: `0 0 0 2px rgba(25, 118, 210, 0.15)`,
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.95rem",
                                                        textAlign: "center",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <span style={{ fontSize: "0.95rem" }}>
                                            {values[col.key] ?? ""}
                                        </span>
                                    )}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default FormFillCombine;